import { Fragment, useState, useEffect } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Box,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup, Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import StepperButtons from '../acquisition/StepperButtons';
import { FINALIZE } from '../../../hooks/useStepNavigations';
import { ee } from '../../../services/earth-engine';
import { Scatter } from 'react-chartjs-2';
import { get as getSatellite } from '../../../common/satellites';
import { useFile, usePreProcessing, useResult } from '../../contexts';
import { makeStyles } from '@material-ui/core/styles';
import { Lyzenga, addpSDB } from '../../../algorithms/bathymetry';
import { splitMasks } from '../../../algorithms/bathymetry/validation';
import { addSPM02, SPM_BAND } from '../../../algorithms/bathymetry/spm';
import { CV_SCHEMES, ENSEMBLE_WEIGHTINGS } from '../../../algorithms/bathymetry/crossValidation';
import { missingPredictors, modelMismatch, readModelFile } from '../../../algorithms/bathymetry/modelStore';
import { presetContextOf } from '../../../algorithms/bathymetry/preset';
import {
  clampTreeParam,
  ENSEMBLE,
  GRADIENT_BOOST,
  isTreeModel,
  PREDICTOR_SETS,
  predictorImage,
  RANDOM_FOREST,
  predictorNames,
  RATIO_BANDS,
  GBT_LOSSES,
  TREE_PARAM_BOUNDS,
  treeParamsOf,
  VARS_PER_SPLIT_MODES,
} from '../../../algorithms/bathymetry/trees';
import { ActivityIndicator, HelpButton } from '../core';
import { mapGeeError } from '../../../algorithms/utils';
import { extentOf } from '../../../common/utils';
import {
  plottedSample,
  sampleTrainingPoints,
  samplesCollection,
  scatterOf,
} from '../../../algorithms/bathymetry/trainingSamples';

const EXT_BIN_SIZE_M = 2;
const EXT_MIN_SAMPLES = 30;
const EXT_R2_THRESHOLD = 0.3;

/**
 * @function createXAxis
 * @description define the chart's X axis characteristics
 * @param {string} title - title of the chart to display
 * **Called by:
 * @see {BathymetryBandsAndAlgorithmSelection} - via the scatterChartOptions1/2 definitions.
 * @memberof module:Bathymetry
 * @return {Object} definition of the X axis (type, position and label)
 */
const createXAxis = (title) => ({
  type: 'linear',
  position: 'bottom',
  scaleLabel: {
    display: !!title,
    labelString: title,
    fontColor: 'red',
    fontSize: 14,
  },
});

/**
 * @function BathymetryBandsAndAlgorithmSelection
 * @description This is fourth step page of bathymetry's acquisition. It allows users to visualize spectral bands,
 * choose processing algorithms, and define threshold values through scatter plot visualizations.
 * Processes training data using Google Earth Engine to calculate correlations between depth and spectral indices.
 * @param {Function} navigate - Function to navigate between steps in the acquisition process
 * **Called by:
 * @see {Bathymetry} - bathymetry's acquisition (index.jsx)
 * @memberof module:Bathymetry
 * @returns {JSX.Element} - component containing interface for band selection and correlation chart visualization
 */
const BathymetryBandsAndAlgorithmSelection = ({ navigate, sessionImportButton }) => {
  const { roiRef, collectionRef } = usePreProcessing();
  const selectedSatelliteIndex = Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'));
  const satellite = getSatellite(selectedSatelliteIndex);
  const type = window.sessionStorage.getItem('typeSatellite');
  const ms =
    type === 'TOA' ? satellite.missions[selectedSatelliteIndex] : satellite.surfaceReflectance[selectedSatelliteIndex];
  const { data, setData } = useResult();
  const { fileData, setFileData } = useFile();
  const bandsState = fileData?.bandsState;
  const [modelType, setModelType] = useState(() => bandsState?.modelType ?? 'Lyzenga');
  const classes = useStyles();
  const blue = ms.bands.blue;
  const green = ms.bands.green;
  const red = ms.bands.red;
  const nir = ms.bands.nir;
  const swir = ms.bands.swir;
  const rededge1 = ms.bands.rededge1;
  const [t] = useTranslation();
  const [thresholdValue, setThresholdValue] = useState(() => bandsState?.thresholdValue ?? 15);
  const [bands, setBands] = useState(() => bandsState?.bands ?? [blue, green, red]);
  const [thresholdRange, setThresholdRange] = useState(() => bandsState?.thresholdRange ?? [0, 100]);
  const [loading, setLoading] = useState(false);
  const [samplingProgress, setSamplingProgress] = useState(null);
  const [dataScatter, setDataScatter] = useState(() => bandsState?.dataScatter ?? [[], []]);
  const [labelScatter, setLabelScatter] = useState(() => bandsState?.labelScatter ?? ['', '']);
  const [axisTitles, setAxisTitles] = useState(() => bandsState?.axisTitles ?? { x1: '', x2: '', y: 'depth' });
  const [trendLines, setTrendLines] = useState(() => bandsState?.trendLines ?? [{}, {}]);
  const [chartError, setChartError] = useState(null);
  const [treeParams, setTreeParams] = useState(() => treeParamsOf(bandsState?.treeParams ?? data?.treeParams));
  const [loadedModel, setLoadedModel] = useState(() => data?.loadedModel ?? null);
  const [modelError, setModelError] = useState(null);
  const treeModel = isTreeModel(modelType);

  useEffect(() => {
    setFileData((prev) => ({
      ...prev,
      bandsState: {
        modelType,
        thresholdValue,
        bands,
        thresholdRange,
        dataScatter,
        labelScatter,
        axisTitles,
        trendLines,
        treeParams,
      },
    }));
  }, [modelType, thresholdValue, bands, thresholdRange, dataScatter, labelScatter, axisTitles, trendLines, treeParams]);
  const redBands = [
    { value: red, label: 'red' },
    { value: nir, label: 'nir' },
    { value: swir, label: 'swir' },
  ];
  if (rededge1) redBands.push({ value: rededge1, label: 'red edge 1' });

  const isNextDisabled = loading || (dataScatter[0].length === 0 && !loadedModel);

  const baseScatterOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { bottom: 20, left: 10 } },
    scales: {
      yAxes: [
        {
          ticks: { beginAtZero: false },
          scaleLabel: {
            display: !!axisTitles.y,
            labelString: axisTitles.y,
            fontColor: 'red',
            fontSize: 14,
          },
        },
      ],
    },
  };

  const scatterChartOptions1 = {
    ...baseScatterOptions,
    scales: {
      ...baseScatterOptions.scales,
      xAxes: [createXAxis(axisTitles.x1)],
    },
  };

  const scatterChartOptions2 = {
    ...baseScatterOptions,
    scales: {
      ...baseScatterOptions.scales,
      xAxes: [createXAxis(axisTitles.x2)],
    },
  };

  /**
   * @function handleFinish
   * @description when the user finishes this step, this function runs and save data from this component
   * **Called by:
   * @see {StepperButtons} - via the onNext prop.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const handleFinish = () => {
    setData((prev) => ({
      ...prev,
      depthLightExtinction: thresholdValue,
      thresholdValue,
      bands,
      modelSelected: modelType,
      rgb: [bands[0], bands[1], bands[2]],
      treeParams: treeModel ? treeParams : null,
    }));
  };

  /**
   * @function updateTreeParam
   * @description Records one calibration parameter of the tree models as the user types it, holding
   * it to the upper bound only. The lower bound is applied by {@link treeParamsOf} where the value
   * is read, because raising a half-typed number to its minimum puts the field out of reach: with a
   * minimum of 100, typing "300" went 100, 1000, 10000.
   * @param {String} name - Parameter name.
   * @param {*} value - The typed value.
   * @see {@link clampTreeParam}
   * **Called by:
   * @see {BathymetryBandsAndAlgorithmSelection} - via each parameter field's onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const updateTreeParam = (name, value) => {
    setTreeParams((prev) => ({ ...prev, [name]: clampTreeParam(name, value, { partial: true }) }));
  };

  /**
   * @function loadModelFile
   * @description Reads a saved model and holds it for this analysis, so the run applies it instead
   * of fitting a new one.
   * The imagery is checked before the model is accepted, not after: a model built on Landsat bands
   * applied to a Sentinel image finds bands of the same names and predicts confidently from the
   * wrong data. The predictor set is checked too, because the same satellite with a different set
   * chosen produces a stack the trees cannot read.
   * @param {Object} event - The file input's change event.
   * **Called by:
   * @see {BathymetryBandsAndAlgorithmSelection} - via the model input's onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const loadModelFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (loaded) => {
      const key = (name) => `forms.acquisition.4.bathymetry.modelStore.${name}`;
      setModelError(null);
      let parsed = null;
      try {
        parsed = JSON.parse(loaded.target.result);
      } catch (error) {
        setModelError(t(key('notJson')));
        return;
      }

      const { model, error } = readModelFile(parsed);
      if (error) {
        setModelError(t(key(error)));
        return;
      }

      const mismatch = modelMismatch(model, presetContextOf(satellite, type, bands));
      if (mismatch) {
        setModelError(t(key('mismatch'), mismatch));
        return;
      }

      const absent = missingPredictors(
        model,
        predictorNames({ bands, missionBands: ms.bands, predictorSet: model.predictorSet }),
      );
      if (absent.length) {
        setModelError(t(key('missingPredictors'), { names: absent.join(', ') }));
        return;
      }

      setLoadedModel(model);
      setModelType(model.model);
      setTreeParams(treeParamsOf({ ...model.params, predictorSet: model.predictorSet }));
      setData((prev) => ({ ...prev, loadedModel: model }));
    };
    reader.readAsText(file);
  };

  /**
   * @function clearLoadedModel
   * @description Drops the loaded model so the next run fits its own again.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const clearLoadedModel = () => {
    setModelError(null);
    setLoadedModel(null);
    setData((prev) => ({ ...prev, loadedModel: null }));
  };

  /**
   * @function updateThresholdValue
   * @description this method is responsible to update the threshold value through useState, it handles the value as not a number throwing an exception
   * @param {string|number} value - The new threshold value typed by the user.
   * **Called by:
   * @see {BathymetryBandsAndAlgorithmSelection} - via the threshold TextField's onChange.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const updateThresholdValue = (value) => {
    if (isNaN(value)) throw new Error('Error reading threshold value');
    setThresholdValue(Number.parseFloat(value));
  };

  /**
   * @function sampleTrainingFromImage
   * @description Samples only the necessary predictor bands from the image at training points.
   * This avoids pulling unnecessary bands and reduces EE memory usage.
   * IMPORTANT: do NOT unmask predictors for training.
   * Keeping masks ensures masked pixels become null in the sample and can be removed later.
   * @param {ee.Image} image - Image to sample, already containing the predictor bands.
   * @param {string[]} sampleBands - Predictor band names to keep before sampling.
   * When the user asked for a validation split, the image is masked to the training pixels before
   * sampling, so a training point sitting in a held-back pixel comes back with null predictors and
   * is dropped. The fit - and the scatter plots and extinction depth drawn from it - never reads a
   * pixel that will be scored. See {@link splitMasks}.
   * The survey is sampled in slices, and the values come back to the browser once: the charts are
   * drawn from them, and the result page fits on them instead of sampling the composite again. See
   * {@link sampleTrainingPoints}.
   * **Called by:
   * @see {reprocessCharts}
   * @memberof module:Bathymetry
   * @returns {Promise<{rows: Array<Array<Number>>, bands: String[], collection: Object}>} The
   *  `[or_depth, ...bands]` rows, and the same samples as an `ee.FeatureCollection`.
   */
  const sampleTrainingFromImage = async (image, sampleBands) => {
    if (!fileData?.matrix) throw new Error('Training data matrix is empty or undefined.');
    const scale = ms.opticalResolution || 30;
    const clipped = image.select(sampleBands).clip(roiRef.current);
    const imageToSample = data?.validationSplit
      ? clipped.updateMask(splitMasks({ scale, crs: fileData.coordinatesSystem }).train)
      : clipped;
    setSamplingProgress(null);
    try {
      const rows = await sampleTrainingPoints({
        image: imageToSample,
        matrix: fileData.trainingMatrix,
        collection: fileData.matrix,
        crs: fileData.coordinatesSystem,
        scale,
        bands: sampleBands,
        onProgress: setSamplingProgress,
      });
      return { rows, bands: sampleBands, collection: samplesCollection(rows, sampleBands) };
    } finally {
      setSamplingProgress(null);
    }
  };

  /**
   * @function getScatterPoints
   * @description One predictor against depth, from samples already in the browser.
   * @param {{rows: Array<Array<Number>>, bands: String[]}} samples - From {@link sampleTrainingFromImage}.
   * @param {string} xField - Name of the predictor column to use as x.
   * **Called by:
   * @see {reprocessCharts}
   * @memberof module:Bathymetry
   * @returns {Array<{x:number,y:number}>}
   */
  const getScatterPoints = (samples, xField) => scatterOf(samples.rows, samples.bands, xField);

  /**
   * @function estimateExtinctionDepthFromR2
   * @description Computes light extinction depth on the client from scatter points. Estimates the light extinction depth by iteratively
   * evaluating the coefficient of determination (R²) for depth-limited subsets of scatter plot points.
   * This avoids extra heavy EE reduceColumns calls over the sampled collection.
   * @param {Array<{x:number,y:number}>} points  -Scatter plot data points.
   * **Called by:
   * @see {reprocessCharts}
   * @memberof module:Bathymetry
   * @returns {number} Estimated light extinction depth in meters.
   */
  const estimateExtinctionDepthFromR2 = (points) => {
    if (!Array.isArray(points) || points.length === 0) return thresholdValue;
    const valid = points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    if (valid.length === 0) return thresholdValue;
    const [, maxDepth] = extentOf(valid.map((p) => p.y));
    if (!Number.isFinite(maxDepth) || maxDepth <= 0) return thresholdValue;
    const pearsonR2 = (arr) => {
      const n = arr.length;
      if (n < 2) return null;
      const sumX = arr.reduce((s, p) => s + p.x, 0);
      const sumY = arr.reduce((s, p) => s + p.y, 0);
      const sumXY = arr.reduce((s, p) => s + p.x * p.y, 0);
      const sumX2 = arr.reduce((s, p) => s + p.x * p.x, 0);
      const sumY2 = arr.reduce((s, p) => s + p.y * p.y, 0);
      const numerator = n * sumXY - sumX * sumY;
      const denomX = n * sumX2 - sumX * sumX;
      const denomY = n * sumY2 - sumY * sumY;
      const denominator = Math.sqrt(denomX * denomY);
      if (!Number.isFinite(denominator) || denominator === 0) return null;
      const r = numerator / denominator;
      return r * r;
    };
    let bestZ = maxDepth;
    for (let z = EXT_BIN_SIZE_M; z <= maxDepth; z += EXT_BIN_SIZE_M) {
      const subset = valid.filter((p) => p.y <= z);
      if (subset.length < EXT_MIN_SAMPLES) continue;
      const r2 = pearsonR2(subset);
      if (r2 != null && r2 > EXT_R2_THRESHOLD) bestZ = z;
    }
    return Number(bestZ.toFixed(2));
  };

  /**
   * @function reducePseudoFromCollection
   * @description Computes pseudo-bathymetry bands for each image in a  collection using the selected bathymetric model and reduces the
   * resulting collection using the 90th percentile reducer.
   * @param {ee.FeatureCollection} col- collection of images
   * @param {number} bdsModel - chosen model to apply bathymetry calculation
   * @see {@link addpSDB} - computes the log-ratio band(s) for each image in the collection.
   * **Called by:
   * @see {reprocessCharts}
   * @memberof module:Bathymetry
   * @returns {ee.Image} Reduced image containing the pseudo-bathymetry bands.
   */
  const reducePseudoFromCollection = (col, bdsModel = 2) => {
    const withP = col.map((img) => {
      if (bdsModel === 2) {
        img = img.addBands(addpSDB(img, bands));
        const selBands = ee.List(['pSDB']);
        return img.select(selBands).toFloat().copyProperties(img, img.propertyNames());
      }
      if (bdsModel === 3) {
        const greenBand = addpSDB(img, [bands[0], bands[1]], 'pSDBgreen');
        const redBand = addpSDB(img, [bands[0], bands[2]], 'pSDBred');
        img = img.addBands([greenBand, redBand]);
        const selBands = ee.List(['pSDBgreen', 'pSDBred']);
        return img.select(selBands).toFloat().copyProperties(img, img.propertyNames());
      }
      throw new Error(`invalid Model: ${bdsModel}`);
    });
    const outP = withP.reduce(ee.Reducer.percentile([90]));
    const suf = ee.String('_p').cat(ee.Number(90).format());
    return bdsModel === 2
      ? outP.select([ee.String('pSDB').cat(suf)], ['pSDB'])
      : outP.select([ee.String('pSDBgreen').cat(suf), ee.String('pSDBred').cat(suf)], ['pSDBgreen', 'pSDBred']);
  };

  /**
   * @function reprocessCharts
   * @description Reprocesses scatter plots with newly selected band combinations
   * @see {@link sampleTrainingFromImage} - samples the predictor bands at training points.
   * @see {@link getScatterPoints} - converts the sampled collection into scatter points.
   * @see {@link estimateExtinctionDepthFromR2} - estimates the light extinction depth from the scatter points.
   * @see {@link linearRegression} - fits the trend line drawn over each scatter plot.
   * @see {@link reducePseudoFromCollection} - pre-computes pseudo-bathymetry bands for composite images.
   * @see {@link predictorImage} - builds the predictor stack of the tree models.
   * The tree models sample only the band ratios here, whichever predictor set is selected for
   * fitting. The scatter plots read nothing else, and the light extinction depth they estimate is a
   * property of the water rather than of the model, so it stays available for every predictor set -
   * including the one that fits on raw bands alone.
   * Unlike Caballero, they do not use the per-collection percentile ratios on composites. A tree
   * fitted on one definition of a feature and applied to another is fitting one variable and
   * predicting on a different one, which matters far more here than for a two-coefficient line.
   * **Called by:
   * @see {BathymetryBandsAndAlgorithmSelection} - via the "reprocess" button's onClick.
   * @memberof module:Bathymetry
   * @returns {void}
   */
  const reprocessCharts = async () => {
    setLoading(true);
    setChartError(null);
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      switch (modelType) {
        case 'Lyzenga':
          const constantBand = ee.Image.constant(1).rename('constant');
          const image = Lyzenga.add_lyzengaPredictors(data.image, bands).addBands(constantBand, null, true);
          const sampled = await sampleTrainingFromImage(image, ['lnBand1', 'lnBand2', 'lnBand3', 'constant']);
          const scatter1 = getScatterPoints(sampled, 'lnBand1');
          const scatter2 = getScatterPoints(sampled, 'lnBand2');
          const z1 = estimateExtinctionDepthFromR2(scatter1);
          const z2 = estimateExtinctionDepthFromR2(scatter2);
          const zExtValue = Math.max(z1, z2);
          setThresholdValue(zExtValue);
          setData((prev) => ({
            ...prev,
            sampledImage: sampled.collection,
            bands,
            modelSelected: modelType,
          }));
          if (scatter1.length === 0 && scatter2.length === 0) {
            setTrendLines([{}, {}]);
            setDataScatter([[], []]);
            setLabelScatter(['', '']);
            setAxisTitles({ x1: '', x2: '', y: 'Depth' });
            return;
          }
          const trendLine1 = linearRegression(scatter1);
          const trendLine2 = linearRegression(scatter2);
          setTrendLines([trendLine1, trendLine2]);
          setDataScatter([plottedSample(scatter1), plottedSample(scatter2)]);
          setLabelScatter(['lnBand1 vs depth', 'lnBand2 vs depth']);
          setAxisTitles({ x1: 'lnBand1', x2: 'lnBand2', y: 'Depth' });
          break;
        case 'Stumpf 2003':
          let workingImage = data.image;
          if (data.typeImageProcess === 'COMPOSITE') {
            const psdbImg = reducePseudoFromCollection(collectionRef.current, 2);
            workingImage = ee.Image(workingImage).addBands(psdbImg, null, true);
          }
          const imgP2s = addSPM02(workingImage.addBands(addpSDB(workingImage, bands)), ms.bands);
          const sampledStumpf = await sampleTrainingFromImage(imgP2s, ['pSDB', SPM_BAND]);
          const scatter = getScatterPoints(sampledStumpf, 'pSDB');
          const zExtValueStumpf = estimateExtinctionDepthFromR2(scatter);
          setThresholdValue(zExtValueStumpf);
          setData((prev) => ({
            ...prev,
            sampledImage: sampledStumpf.collection,
            bands,
            modelSelected: modelType,
          }));
          const trendLine = linearRegression(scatter);
          setTrendLines([trendLine, {}]);
          setDataScatter([plottedSample(scatter), []]);
          setLabelScatter(['pSDB vs Depth', '']);
          setAxisTitles({ x1: 'pSDB', x2: '', y: 'Depth' });
          break;
        case 'Caballero & Stumpf 2020':
          let workingImageCaballero = data.image;
          if (data.typeImageProcess === 'COMPOSITE') {
            const psdbImg = reducePseudoFromCollection(collectionRef.current, 3);
            workingImageCaballero = ee.Image(workingImageCaballero).addBands(psdbImg, null, true);
          }
          const bandGreen = addpSDB(workingImageCaballero, [bands[0], bands[1]], 'pSDBgreen');
          const bandRed = addpSDB(workingImageCaballero, [bands[0], bands[2]], 'pSDBred');
          const imgP3s = addSPM02(workingImageCaballero.addBands([bandGreen, bandRed]), ms.bands);
          const sampledCaballero = await sampleTrainingFromImage(imgP3s, ['pSDBgreen', 'pSDBred', SPM_BAND]);
          const scatter1Caballero = getScatterPoints(sampledCaballero, 'pSDBgreen');
          const scatter2Caballero = getScatterPoints(sampledCaballero, 'pSDBred');
          const z1C = estimateExtinctionDepthFromR2(scatter1Caballero);
          const z2C = estimateExtinctionDepthFromR2(scatter2Caballero);
          const zExtValueCaballero = Math.max(z1C, z2C);
          setThresholdValue(zExtValueCaballero);
          setData((prev) => ({
            ...prev,
            sampledImage: sampledCaballero.collection,
            bands,
            modelSelected: modelType,
          }));
          const trendLine1Caballero = linearRegression(scatter1Caballero);
          const trendLine2Caballero = linearRegression(scatter2Caballero);
          setTrendLines([trendLine1Caballero, trendLine2Caballero]);
          setDataScatter([plottedSample(scatter1Caballero), plottedSample(scatter2Caballero)]);
          setLabelScatter(['pSDBgreen vs Depth', 'pSDBred vs Depth']);
          setAxisTitles({ x1: 'pSDBgreen', x2: 'pSDBred', y: 'Depth' });
          break;
        case RANDOM_FOREST:
        case GRADIENT_BOOST:
        case ENSEMBLE: {
          const treeContext = {
            bands,
            missionBands: ms.bands,
            predictorSet: treeParams.predictorSet,
            scale: ms.opticalResolution || 30,
            crs: fileData.coordinatesSystem,
          };
          /**
           * These two charts read the band ratios and nothing else, so only the ratios are sampled.
           * Building the whole predictor stack here cost a reprojected neighbourhood pass over the
           * composite and exhausted Earth Engine's memory before a single point was drawn. The full
           * stack belongs on the result page, which is where it is fitted - see
           * {@link predictorImage}.
           */
          const ratioContext = { ...treeContext, predictorSet: PREDICTOR_SETS.RATIOS };
          const sampledTrees = await sampleTrainingFromImage(predictorImage(data.image, ratioContext), RATIO_BANDS);
          const scatterGreen = getScatterPoints(sampledTrees, 'pSDBgreen');
          const scatterRed = getScatterPoints(sampledTrees, 'pSDBred');
          setThresholdValue(
            Math.max(estimateExtinctionDepthFromR2(scatterGreen), estimateExtinctionDepthFromR2(scatterRed)),
          );
          setData((prev) => ({
            ...prev,
            sampledImage: sampledTrees.collection,
            bands,
            modelSelected: modelType,
            treeParams,
          }));
          if (scatterGreen.length === 0 && scatterRed.length === 0) {
            setTrendLines([{}, {}]);
            setDataScatter([[], []]);
            setLabelScatter(['', '']);
            setAxisTitles({ x1: '', x2: '', y: 'Depth' });
            return;
          }
          setTrendLines([linearRegression(scatterGreen), linearRegression(scatterRed)]);
          setDataScatter([plottedSample(scatterGreen), plottedSample(scatterRed)]);
          setLabelScatter(['pSDBgreen vs Depth', 'pSDBred vs Depth']);
          setAxisTitles({ x1: 'pSDBgreen', x2: 'pSDBred', y: 'Depth' });
          break;
        }
        default:
          throw new Error(`unrecognized model type: ${modelType}`);
      }
    } catch (e) {
      console.error('reprocessCharts error:', e);
      setTrendLines([{}, {}]);
      setDataScatter([[], []]);
      setLabelScatter(['', '']);
      setAxisTitles({ x1: '', x2: '', y: 'Depth' });
      setChartError(
        mapGeeError(e, t, 'genericError', isTreeModel(modelType) ? { errorMemory: 'treeErrorMemory' } : {}),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * @function linearRegression
   * @description Calculates linear regression for scatter plots
   * @param {Array<{x:number,y:number}>} points - Scatter plot data points.
   * **Called by:
   * @see {reprocessCharts}
   * @memberof module:Bathymetry
   * @returns {Object} line for chart.js
   */
  const linearRegression = (points) => {
    if (!points || points.length < 2) return {};
    const n = points.length;
    const sumX = points.reduce((sum, p) => sum + p.x, 0);
    const sumY = points.reduce((sum, p) => sum + p.y, 0);
    const sumXY = points.reduce((sum, p) => sum + p.x * p.y, 0);
    const sumX2 = points.reduce((sum, p) => sum + p.x * p.x, 0);
    const sumY2 = points.reduce((sum, p) => sum + p.y * p.y, 0);
    const denominator = n * sumX2 - sumX * sumX;
    const slope = denominator === 0 ? 0 : (n * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / n;
    const meanY = sumY / n;
    const ssTotal = sumY2 - n * meanY * meanY;
    const ssRes = points.reduce((sum, p) => {
      const predicted = slope * p.x + intercept;
      return sum + Math.pow(p.y - predicted, 2);
    }, 0);
    const r2 = ssTotal === 0 ? 0 : 1 - ssRes / ssTotal;
    const [minX, maxX] = extentOf(points.map((p) => p.x));
    const [lowestY, highestY] = extentOf(points.map((p) => p.y));
    const minY = Number(lowestY.toFixed(2));
    const maxY = Number(highestY.toFixed(2));
    setThresholdRange([minY, maxY]);
    return {
      type: 'line',
      label: `Trend Line (R² = ${r2.toFixed(3)})`,
      data: [
        { x: minX, y: slope * minX + intercept },
        { x: maxX, y: slope * maxX + intercept },
      ],
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 2,
      fill: false,
      pointRadius: 0,
    };
  };

  return (
    <Box>
      <HelpButton onClickFunction={() => {}} extra={sessionImportButton} />
      <div
        style={{
          margin: '0 20px 0 20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <strong style={{ margin: 15, display: 'flex', justifyContent: 'center' }}>
          {t('forms.acquisition.4.bathymetry.modelSelection')}:
        </strong>
        <ToggleButtonGroup
          value={modelType}
          exclusive
          onChange={(e, newValue) => {
            if (!newValue) return;
            setModelType(newValue);
          }}
          style={{ marginBottom: 20 }}
          color="primary">
          <ToggleButton value="Lyzenga">Lyzenga</ToggleButton>
          <ToggleButton value="Stumpf 2003">Stumpf 2003</ToggleButton>
          <ToggleButton value="Caballero & Stumpf 2020">Caballero & Stumpf 2020</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          value={modelType}
          exclusive
          onChange={(e, newValue) => {
            if (!newValue) return;
            setModelType(newValue);
          }}
          style={{ marginBottom: 20 }}
          size="small"
          color="primary">
          <ToggleButton value={RANDOM_FOREST}>{RANDOM_FOREST}</ToggleButton>
          <ToggleButton value={GRADIENT_BOOST}>{GRADIENT_BOOST}</ToggleButton>
          <ToggleButton value={ENSEMBLE}>{ENSEMBLE}</ToggleButton>
        </ToggleButtonGroup>
        {treeModel && (
          <Paper variant="outlined" style={{ padding: 16, borderRadius: 10, marginBottom: 20, maxWidth: 520 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
              {t('forms.acquisition.4.bathymetry.treeCalibration')}
            </Typography>
            <Typography variant="caption" color="textSecondary" component="p" style={{ marginBottom: 12 }}>
              {t('forms.acquisition.4.bathymetry.treeCalibrationHelp')}
            </Typography>
            {loadedModel ? (
              <Alert severity="info" onClose={clearLoadedModel} style={{ marginBottom: 12 }}>
                {t('forms.acquisition.4.bathymetry.modelStore.loaded', {
                  model: loadedModel.model,
                  date: loadedModel.trainedOn?.imageDate ?? '—',
                  samples: loadedModel.trainedOn?.samples ?? '—',
                })}
              </Alert>
            ) : (
              <Box style={{ marginBottom: 12 }}>
                <Button variant="outlined" size="small" color="primary" component="label">
                  {t('forms.acquisition.4.bathymetry.modelStore.load')}
                  <input type="file" accept="application/json,.json" hidden onChange={loadModelFile} />
                </Button>
                <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 6 }}>
                  {t('forms.acquisition.4.bathymetry.modelStore.loadHelp')}
                </Typography>
                {modelError && (
                  <Alert severity="error" onClose={() => setModelError(null)} style={{ marginTop: 8 }}>
                    {modelError}
                  </Alert>
                )}
              </Box>
            )}
            <Box display="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {modelType !== GRADIENT_BOOST && (
                <Fragment>
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeRfTrees')}
                    value={treeParams.rfNumberOfTrees}
                    inputProps={TREE_PARAM_BOUNDS.rfNumberOfTrees}
                    onChange={(e) => updateTreeParam('rfNumberOfTrees', e.target.value)}
                  />
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeRfVarsPerSplit')}
                    value={treeParams.rfVariablesPerSplit}
                    onChange={(e) => updateTreeParam('rfVariablesPerSplit', e.target.value)}>
                    {VARS_PER_SPLIT_MODES.map((mode) => (
                      <MenuItem key={mode} value={mode}>
                        {t(`forms.acquisition.4.bathymetry.treeVarsMode.${mode}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeRfMinLeaf')}
                    value={treeParams.rfMinLeafPopulation}
                    inputProps={TREE_PARAM_BOUNDS.rfMinLeafPopulation}
                    onChange={(e) => updateTreeParam('rfMinLeafPopulation', e.target.value)}
                  />
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeRfBagFraction')}
                    value={treeParams.rfBagFraction}
                    inputProps={TREE_PARAM_BOUNDS.rfBagFraction}
                    onChange={(e) => updateTreeParam('rfBagFraction', e.target.value)}
                  />
                </Fragment>
              )}
              {modelType !== RANDOM_FOREST && (
                <Fragment>
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeGbtTrees')}
                    value={treeParams.gbtNumberOfTrees}
                    inputProps={TREE_PARAM_BOUNDS.gbtNumberOfTrees}
                    onChange={(e) => updateTreeParam('gbtNumberOfTrees', e.target.value)}
                  />
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeGbtShrinkage')}
                    value={treeParams.gbtShrinkage}
                    inputProps={TREE_PARAM_BOUNDS.gbtShrinkage}
                    onChange={(e) => updateTreeParam('gbtShrinkage', e.target.value)}
                  />
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeGbtSamplingRate')}
                    value={treeParams.gbtSamplingRate}
                    inputProps={TREE_PARAM_BOUNDS.gbtSamplingRate}
                    onChange={(e) => updateTreeParam('gbtSamplingRate', e.target.value)}
                  />
                  <TextField
                    type="number"
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeGbtMaxNodes')}
                    value={treeParams.gbtMaxNodes}
                    inputProps={TREE_PARAM_BOUNDS.gbtMaxNodes}
                    onChange={(e) => updateTreeParam('gbtMaxNodes', e.target.value)}
                  />
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    label={t('forms.acquisition.4.bathymetry.treeGbtLoss')}
                    value={treeParams.gbtLoss}
                    onChange={(e) => updateTreeParam('gbtLoss', e.target.value)}>
                    {GBT_LOSSES.map((loss) => (
                      <MenuItem key={loss} value={loss}>
                        {loss}
                      </MenuItem>
                    ))}
                  </TextField>
                </Fragment>
              )}
              {modelType === ENSEMBLE && treeParams.cvScheme === CV_SCHEMES.NONE && (
                <TextField
                  type="number"
                  size="small"
                  variant="outlined"
                  label={t('forms.acquisition.4.bathymetry.treeEnsembleWeight')}
                  value={treeParams.ensembleWeightRf}
                  inputProps={TREE_PARAM_BOUNDS.ensembleWeightRf}
                  onChange={(e) => updateTreeParam('ensembleWeightRf', e.target.value)}
                />
              )}
            </Box>
            <Typography variant="caption" color="textSecondary" component="p" style={{ margin: '14px 0 2px' }}>
              {t('forms.acquisition.4.bathymetry.treePredictors')}
            </Typography>
            <Typography variant="caption" color="textSecondary" component="p" style={{ marginBottom: 8 }}>
              {t('forms.acquisition.4.bathymetry.treePredictorsHelp')}
            </Typography>
            {/**
             * Locked while a model is loaded: the trees were grown on one set of predictors and
             * cannot read another. Choosing a different one reached Earth Engine and failed there
             * with "Image.classify: Missing bands" and fifteen names, which arrived at the user as
             * "an unexpected error".
             */}
            <ToggleButtonGroup
              value={treeParams.predictorSet}
              exclusive
              disabled={Boolean(loadedModel)}
              onChange={(e, newValue) => {
                if (newValue) setTreeParams((prev) => ({ ...prev, predictorSet: newValue }));
              }}
              size="small">
              <ToggleButton value={PREDICTOR_SETS.FULL}>
                {t('forms.acquisition.4.bathymetry.treePredictorsFull')}
              </ToggleButton>
              <ToggleButton value={PREDICTOR_SETS.RATIOS}>
                {t('forms.acquisition.4.bathymetry.treePredictorsRatios')}
              </ToggleButton>
              <ToggleButton value={PREDICTOR_SETS.BANDS}>
                {t('forms.acquisition.4.bathymetry.treePredictorsBands')}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography
              variant="caption"
              color="textSecondary"
              component="p"
              style={{ margin: '16px 0 2px', fontWeight: 600 }}>
              {t('forms.acquisition.4.bathymetry.treeValidation')}
            </Typography>
            <Typography variant="caption" color="textSecondary" component="p" style={{ marginBottom: 10 }}>
              {t('forms.acquisition.4.bathymetry.treeValidationHelp')}
            </Typography>
            <Box display="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <TextField
                select
                size="small"
                variant="outlined"
                label={t('forms.acquisition.4.bathymetry.treeCvScheme')}
                value={treeParams.cvScheme}
                onChange={(e) => updateTreeParam('cvScheme', e.target.value)}>
                {Object.keys(CV_SCHEMES).map((scheme) => (
                  <MenuItem key={scheme} value={scheme}>
                    {t(`forms.acquisition.4.bathymetry.treeCvSchemes.${scheme}`)}
                  </MenuItem>
                ))}
              </TextField>
              {treeParams.cvScheme !== CV_SCHEMES.NONE && (
                <TextField
                  type="number"
                  size="small"
                  variant="outlined"
                  label={t('forms.acquisition.4.bathymetry.treeCvFolds')}
                  value={treeParams.cvFolds}
                  inputProps={TREE_PARAM_BOUNDS.cvFolds}
                  onChange={(e) => updateTreeParam('cvFolds', e.target.value)}
                />
              )}
              {treeParams.cvScheme === CV_SCHEMES.BLOCK && (
                <TextField
                  type="number"
                  size="small"
                  variant="outlined"
                  label={t('forms.acquisition.4.bathymetry.treeCvBlock')}
                  value={treeParams.cvBlockSizeM}
                  inputProps={TREE_PARAM_BOUNDS.cvBlockSizeM}
                  onChange={(e) => updateTreeParam('cvBlockSizeM', e.target.value)}
                />
              )}
              <TextField
                type="number"
                size="small"
                variant="outlined"
                label={t('forms.acquisition.4.bathymetry.treeStratifyBin')}
                value={treeParams.stratifyBinM}
                inputProps={TREE_PARAM_BOUNDS.stratifyBinM}
                onChange={(e) => updateTreeParam('stratifyBinM', e.target.value)}
              />
              <TextField
                type="number"
                size="small"
                variant="outlined"
                label={t('forms.acquisition.4.bathymetry.treeSamplesPerBin')}
                value={treeParams.samplesPerBin}
                inputProps={TREE_PARAM_BOUNDS.samplesPerBin}
                onChange={(e) => updateTreeParam('samplesPerBin', e.target.value)}
              />
              {modelType === ENSEMBLE && treeParams.cvScheme !== CV_SCHEMES.NONE && (
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label={t('forms.acquisition.4.bathymetry.treeEnsembleWeighting')}
                  value={treeParams.ensembleWeighting}
                  onChange={(e) => updateTreeParam('ensembleWeighting', e.target.value)}>
                  {Object.keys(ENSEMBLE_WEIGHTINGS).map((weighting) => (
                    <MenuItem key={weighting} value={weighting}>
                      {t(`forms.acquisition.4.bathymetry.treeWeightings.${weighting}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
          </Paper>
        )}
        <FormLabel component="span" className={classes.label}>
          {t('forms.acquisition.4.bathymetry.band')} A: {bands[0]} (blue)
        </FormLabel>
        <FormLabel component="span" className={classes.label} style={{ marginTop: 10 }}>
          {t('forms.acquisition.4.bathymetry.band')} B: {bands[1]} (green)
        </FormLabel>
        <FormControl
          component="fieldset"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 20,
          }}>
          <FormLabel component="span" className={classes.label}>
            {t('forms.acquisition.4.bathymetry.band')} C:
          </FormLabel>
          <RadioGroup
            row
            name="bandC"
            value={bands[2] || ''}
            onChange={(e) => setBands([bands[0], bands[1], e.target.value])}>
            {redBands.map((band) => (
              <FormControlLabel
                key={band.value}
                value={band.value}
                disabled={modelType === 'Stumpf 2003'}
                control={<Radio />}
                label={`${band.value} (${band.label})`}
                className={classes.smallLabel}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Button
          variant="contained"
          onClick={reprocessCharts}
          style={{ cursor: 'pointer', marginBottom: 20, marginTop: 20 }}>
          {t('forms.acquisition.4.bathymetry.updateCharts')}
        </Button>
        {chartError && (
          <Box className={classes.errorContainer}>
            <Alert
              severity="error"
              style={{ width: '100%' }}
              action={
                <Button color="inherit" size="small" onClick={() => setChartError(null)}>
                  {t('forms.acquisition.3.bathymetry.compositeRetry')}
                </Button>
              }>
              {chartError}
            </Alert>
          </Box>
        )}
      </div>
      {loading && (
        <ActivityIndicator
          forceShow={true}
          title={
            samplingProgress?.total > 1
              ? t('forms.core.loading.chartsProgress', samplingProgress)
              : t('forms.core.loading.charts')
          }
        />
      )}
      {dataScatter[0].length > 0 && (
        <div
          style={{
            margin: '0 20px 0 20px',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <div className="graph-div">
            <Scatter
              key="scatter1"
              options={scatterChartOptions1}
              datasetKeyProvider={() => `scatter1-${axisTitles.x}-${modelType}`}
              data={{
                datasets: [
                  {
                    label: `${labelScatter[0]}-points`,
                    data: dataScatter[0],
                    backgroundColor: 'rgba(255, 99, 132, 1)',
                  },
                  { ...trendLines[0], label: `${labelScatter[0]}-trend` },
                ],
              }}
            />
          </div>
          {dataScatter[1].length > 0 && (
            <div className="graph-div" style={{ marginTop: 24 }}>
              <Scatter
                key="scatter2"
                datasetKeyProvider={() => `scatter2-${axisTitles.x2}-${modelType}`}
                options={scatterChartOptions2}
                data={{
                  datasets: [
                    {
                      label: `${labelScatter[1]}-points`,
                      data: dataScatter[1],
                      backgroundColor: 'rgba(255, 99, 132, 1)',
                    },
                    { ...trendLines[1], label: `${labelScatter[1]}-trend` },
                  ],
                }}
              />
            </div>
          )}
          <div width="100%" align="center">
            <b>{t('forms.acquisition.4.bathymetry.thresholdSelection')}</b>:
          </div>
          <div width="100%" align="center" style={{ display: 'flex' }}>
            <span>{thresholdRange[0]}</span>
            <input
              style={{ width: '100%' }}
              type="range"
              min={thresholdRange[0]}
              max={thresholdRange[1]}
              step="0.01"
              id="threshold_field"
              value={thresholdValue}
              onChange={(e) => updateThresholdValue(e.target.value)}
            />
            <p>{thresholdValue}</p>
            <span>{thresholdRange[1]}</span>
          </div>

          <div align="center" id="display_threshold"></div>
        </div>
      )}
      <StepperButtons
        navigate={navigate}
        nextText={t('forms.acquisition.4.next')}
        onNext={handleFinish}
        nextDisabled={isNextDisabled}
        nextTarget={FINALIZE}
      />
    </Box>
  );
};

/**
 * @function useStyles-BathymetryBandsAndAlgorithmSelection
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles .
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Bathymetry
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles({
  label: {
    fontSize: '1rem',
  },
  smallLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
    },
  },
  errorContainer: {
    width: '100%',
    marginBottom: 16,
  },
});

export default BathymetryBandsAndAlgorithmSelection;
